import React, { useEffect, useMemo } from 'react';
import { Line } from '@react-three/drei';

import { Point2D, Point3D } from '@paragon/geometry-library';
import { forceArray, getModifierKeys, notNullish } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../components/hooks';
import {
  convertFromDesignToLayoutCoordinates,
  convertFromLayoutToDesignCoordinates,
} from '../library/layout-geometry/coordinates';
import { getSelectionWindowMode } from '../library/selection/window-tool/modes';
import { SelectionWindow } from '../library/selection/window-tool/SelectionWindow';
import { getActiveBulkSelectionWindowToolTargets } from '../library/selection/window-tool/targetSelectors';
import {
  ensureSelectionWindowToolIsActive,
  ensureSelectionWindowToolIsDisabled,
  ensureSelectionWindowToolIsEnabled,
  selectItems,
} from '../store/actions/selectActions';
import { updatePickingObjectInfoForChosenTrussEnvelopes } from '../store/actions/trussGroups';
import { isLayoutCameraInUse } from '../store/selectors/layoutModeSelectors';
import { getPickingObjectInfo } from '../store/selectors/pickingSelectors';
import { threeJsDom } from './util/events/ThreeJSDom';
import { EventPosition, GlobalThreeJSDomObjectEvent } from './util/events/types';

type State = { pointA: Point2D | null; pointB: Point2D | null };
const INITIAL_STATE: State = { pointA: null, pointB: null };

export function SelectionWindowVisualsAndEventListeners() {
  const dispatch = useAppDispatch();

  const isEnabled = useAppSelector((state) => state.selections.isSelectionWindowToolEnabled);

  const useLayoutCoordinates = useAppSelector(isLayoutCameraInUse);
  const targetObjectsWithShapes = useAppSelector(getActiveBulkSelectionWindowToolTargets);

  const mode = useAppSelector(getSelectionWindowMode);
  const settings = useAppSelector((state) => state.selections.selectionWindowSettings);

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const [{ pointA, pointB }, setPoints] = React.useState(INITIAL_STATE);

  const selectionWindow = useMemo(
    () => (pointA && pointB ? new SelectionWindow(pointA, pointB) : null),
    [pointA, pointB],
  );

  const get2DPosition = useMemo(
    () => (event: { eventPosition: EventPosition }) =>
      useLayoutCoordinates
        ? convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D()
        : Point3D.fromObject(event.eventPosition.worldPosition).projectTo2D(),

    [useLayoutCoordinates],
  );

  useEffect(() => {
    if (!isEnabled) return;

    function onSceneMouseDown(event: GlobalThreeJSDomObjectEvent) {
      if (event.metadata.ctrlKey) {
        dispatch(ensureSelectionWindowToolIsEnabled());
        setPoints({ pointA: get2DPosition(event), pointB: null });
      }
    }

    threeJsDom.addGlobalListener('mousedown', onSceneMouseDown);
    return () => void threeJsDom.removeGlobalListener('mousedown', onSceneMouseDown);
  }, [dispatch, get2DPosition, isEnabled, pickingObjectInfo]);

  useEffect(() => {
    // this effect is registered even when the tool is not enabled because "mouse movement when ctrl is held" is
    //   currently how we trigger enabling the tool

    function onSceneMouseMove(event: GlobalThreeJSDomObjectEvent) {
      if (event.metadata.ctrlKey) {
        setPoints((state) => (state.pointA == null ? state : { ...state, pointB: get2DPosition(event) }));

        // When picking objects (as opposed to selecting), the receiver should know what to filter for
        if (pointA && !pickingObjectInfo) dispatch(ensureSelectionWindowToolIsActive());
        else dispatch(ensureSelectionWindowToolIsEnabled());
      } else if (isEnabled) {
        dispatch(ensureSelectionWindowToolIsDisabled());
      }
    }

    threeJsDom.addGlobalListener('mousemove', onSceneMouseMove);
    return () => void threeJsDom.removeGlobalListener('mousemove', onSceneMouseMove);
  }, [dispatch, get2DPosition, isEnabled, pointA, pickingObjectInfo]);

  useEffect(() => {
    if (!isEnabled) return;

    function onKeyUp(event: KeyboardEvent) {
      if (getModifierKeys(event).ctrlKey !== true) {
        dispatch(ensureSelectionWindowToolIsDisabled());
      }
    }

    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, [dispatch, isEnabled]);

  useEffect(() => {
    function onSceneMouseUp(event: GlobalThreeJSDomObjectEvent) {
      dispatch(ensureSelectionWindowToolIsDisabled());
      setPoints({ pointA: null, pointB: null });

      if (event.metadata.ctrlKey && selectionWindow) {
        for (const type of Object.keys(targetObjectsWithShapes)) {
          const itemsWithinWindow = (targetObjectsWithShapes[type as keyof typeof targetObjectsWithShapes] ?? [])
            .filter(([_, shapes]) =>
              forceArray(shapes)
                .filter(notNullish)
                .some((shape) => selectionWindow.overlaps(shape)),
            )
            .map(([item]) => item);

          if (itemsWithinWindow.length === 0) continue;

          // Picking mode does not take into account the multi-select settings, since picking already knows what type of
          // objects it is looking for
          if (pickingObjectInfo) {
            switch (pickingObjectInfo.type) {
              case 'pickingTrussEnvelopesForTrussGroup':
                if (type !== 'trussEnvelope') continue;
                dispatch(updatePickingObjectInfoForChosenTrussEnvelopes(itemsWithinWindow.map((item) => item.guid)));
                break;
            }
          } else {
            // Regular selection mode should take into account the multi-select settings, so if the type of object is not
            // what is currently filtered for, skip it
            if (settings[mode] !== type) continue;

            dispatch(selectItems(itemsWithinWindow, type, { intent: 'toggle', exclusive: false }));
          }
        }
      }
    }

    threeJsDom.addGlobalListener('mouseup', onSceneMouseUp);
    return () => void threeJsDom.removeGlobalListener('mouseup', onSceneMouseUp);
  }, [dispatch, mode, selectionWindow, settings, targetObjectsWithShapes, useLayoutCoordinates, pickingObjectInfo]);

  return selectionWindow ? <SelectionWindowVisual window={selectionWindow} layoutMode={useLayoutCoordinates} /> : null;
}

function SelectionWindowVisual(props: { window: SelectionWindow; layoutMode: boolean }) {
  return props.window.edges.map((edge, i) => {
    let { basePoint, endPoint } = edge.to3D();

    if (props.layoutMode) {
      basePoint = convertFromLayoutToDesignCoordinates(basePoint);
      endPoint = convertFromLayoutToDesignCoordinates(endPoint);
    }

    return <Line key={i} dashed color={0xffffff} points={[basePoint.toArray(), endPoint.toArray()]} />;
  });
}
